import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, FileImage, FileText, Loader2, Trash2, Eye, Download, Upload } from "lucide-react";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { downloadBlob } from "../../utils/exporters";

const ACCEPT = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 5;
const size = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });

/** Evidence files for one control: list with view/download/delete + upload (admins). Files live in object storage. */
export default function EvidencePanel({ tenantId, controlId, files = [], isAdmin, onChange, compact = false }) {
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(null);
  const input = useRef(null);

  const open = async (f, download) => {
    setBusy(f.id);
    try {
      const blob = await authApi.compliance.evidenceBlob(f.id);
      if (download) downloadBlob(blob, f.name, f.content_type);
      else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      toast.error("Could not open evidence", { description: errorMessage(err) });
    } finally {
      setBusy("");
    }
  };
  const remove = async (f) => {
    setBusy(f.id);
    try {
      await authApi.compliance.deleteEvidence(f.id);
      toast.success(`${f.name} removed`);
      onChange(files.filter((x) => x.id !== f.id));
    } catch (err) {
      toast.error("Could not delete", { description: errorMessage(err) });
    } finally {
      setBusy("");
    }
  };
  const upload = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    let current = files;
    for (const file of picked) {
      if (!ACCEPT.includes(file.type)) {
        toast.error(`${file.name}: only PNG, JPG, WebP or PDF`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: larger than 10 MB`);
        continue;
      }
      if (current.length >= MAX_FILES) {
        toast.error(`At most ${MAX_FILES} files per control`);
        break;
      }
      setProgress(file.name);
      try {
        const saved = await authApi.compliance.uploadEvidence(tenantId, controlId, file);
        current = [...current, saved];
        onChange(current);
        toast.success(`${file.name} attached`);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`, { description: errorMessage(err) });
      }
    }
    setProgress(null);
    if (input.current) input.current.value = "";
  };

  return (
    <div className={`cis-evidence ${compact ? "compact" : ""}`} data-testid={`cis-evidence-${controlId}`}>
      <div className="cis-evidence-head">
        <span>
          <Paperclip size={12} /> Evidence <span className="count">{files.length}/{MAX_FILES}</span>
        </span>
        {isAdmin && (
          <>
            <input ref={input} type="file" accept={ACCEPT.join(",")} multiple hidden onChange={(e) => upload(e.target.files)} data-testid={`cis-evidence-input-${controlId}`} />
            <button type="button" className="tb-btn sm" disabled={!!progress || files.length >= MAX_FILES} onClick={() => input.current?.click()} data-testid={`cis-evidence-upload-${controlId}`}>
              {progress ? <Loader2 size={12} className="spin" /> : <Upload size={12} />} {progress ? `Uploading ${progress}...` : "Attach screenshot / PDF"}
            </button>
          </>
        )}
      </div>
      {files.length === 0 && <p className="muted small" data-testid={`cis-evidence-empty-${controlId}`}>No evidence attached yet.{isAdmin ? " PNG, JPG, WebP or PDF up to 10 MB." : ""}</p>}
      {files.length > 0 && (
        <ul className="cis-evidence-list">
          {files.map((f) => (
            <li key={f.id} data-testid={`cis-evidence-file-${f.id}`}>
              {f.content_type === "application/pdf" ? <FileText size={14} /> : <FileImage size={14} />}
              <span className="cis-evidence-name" title={f.name}>
                {f.name}
              </span>
              <span className="muted small">
                {size(f.size)} · {f.by_name} · {fmt(f.at)}
              </span>
              <span className="cis-evidence-actions">
                <button type="button" className="icon-btn" title="View" disabled={busy === f.id} onClick={() => open(f, false)} data-testid={`cis-evidence-view-${f.id}`}>
                  {busy === f.id ? <Loader2 size={13} className="spin" /> : <Eye size={13} />}
                </button>
                <button type="button" className="icon-btn" title="Download" disabled={busy === f.id} onClick={() => open(f, true)} data-testid={`cis-evidence-download-${f.id}`}>
                  <Download size={13} />
                </button>
                {isAdmin && (
                  <button type="button" className="icon-btn danger" title="Delete" disabled={busy === f.id} onClick={() => remove(f)} data-testid={`cis-evidence-delete-${f.id}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
